import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { TWButton, TWConfirmModal, TWInput, TWModal, TWSelect } from '../../../components/ui';
import fleetApi from '../../../services/fleetApi';
import { fleetErrorText } from '../use-fleet-poll';
import { APPS, COMMAND_TYPES, needsTypedConfirm, validateCommandArgs } from '../fleet-utils';

const typeOptions = (endMaintenanceSupported) =>
  COMMAND_TYPES.filter((c) => endMaintenanceSupported || c.type !== 'end_maintenance').map((c) => ({
    value: c.type,
    label: c.label,
  }));
const PACKAGE_LABELS = Object.fromEntries(APPS.map((a) => [a.packagename, a.label]));
const ARG_LABELS = {
  package: 'App',
  percent: 'Percent',
  lines: 'Lines',
  heartbeatseconds: 'Heartbeat (seconds)',
  telemetryseconds: 'Telemetry (seconds)',
};
const RISKY_DESCRIPTION =
  'The device is interrupted and a device in the middle of an order can lose it.';
const NO_TARGETS = [];

const devicesText = (n) => `${n} ${n === 1 ? 'device' : 'devices'}`;

// validateCommandArgs names the offending argument first ("percent must be …").
const errorFieldOf = (command, error) =>
  command.args.find((arg) => error.startsWith(arg.name + ' '))?.name || command.args[0]?.name;

// The command sheet: pick a type, fill its arguments, send to one device or to
// a resolved set. Risky types confirm first; sets above the threshold need the
// count typed. The confirm modal is a sibling of the sheet; while it is open
// the sheet is not dismissible, so Escape and outside clicks reach only the
// confirm.
const CommandSheet = ({ open, onClose, targets, onSent, initialType, endMaintenanceSupported = false }) => {
  const deviceIds = targets?.deviceIds || NO_TARGETS;
  const label = targets?.label || '';
  const count = deviceIds.length;

  const [type, setType] = useState(initialType || COMMAND_TYPES[0].type);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(COMMAND_TYPES.some((c) => c.type === initialType) ? initialType : COMMAND_TYPES[0].type);
    setValues({});
    setError(null);
    setTyped('');
    setConfirmOpen(false);
  }, [open, initialType]);

  // Escape while the confirm is up closes the confirm only. Both HUI dialogs
  // listen on window and the first listener wins; the sheet's own is a no-op
  // here, so the capture listener below takes the key first.
  useEffect(() => {
    if (!open || !confirmOpen || sending) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, confirmOpen, sending]);

  const command = COMMAND_TYPES.find((c) => c.type === type) || COMMAND_TYPES[0];
  const typedOk = !needsTypedConfirm(count) || typed === String(count);
  const canSend = count > 0 && typedOk && !sending;

  const changeType = (next) => {
    setType(next);
    setValues({});
    setError(null);
  };

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const deliver = async (args) => {
    setSending(true);
    try {
      if (count === 1) {
        await fleetApi.sendCommand(deviceIds[0], { type: command.type, args });
        message.success('Command queued for 1 device');
      } else {
        const res = await fleetApi.sendBulk({
          type: command.type,
          args,
          deviceids: deviceIds,
          confirmcount: count,
        });
        const answer = res?.data?.data || {};
        const skipped = Array.isArray(answer.skipped) ? answer.skipped.length : 0;
        const created = typeof answer.created === 'number' ? answer.created : count - skipped;
        if (skipped > 0) message.warning(`${created} queued, ${skipped} skipped`);
        else message.success(`${created} queued`);
      }
      setConfirmOpen(false);
      if (onSent) onSent();
      onClose();
    } catch (err) {
      setConfirmOpen(false);
      message.error(fleetErrorText(err, 'Could not send the command'));
    } finally {
      setSending(false);
    }
  };

  const validated = () => validateCommandArgs(command.type, values);

  const onSend = () => {
    const result = validated();
    if (!result.ok) {
      setError({ field: errorFieldOf(command, result.error), text: result.error });
      return;
    }
    setError(null);
    if (command.risky) setConfirmOpen(true);
    else deliver(result.args);
  };

  const onConfirm = () => {
    const result = validated();
    if (result.ok) deliver(result.args);
  };

  const renderArg = (arg) => {
    const fieldError = error && error.field === arg.name ? error.text : undefined;
    const argLabel = ARG_LABELS[arg.name] || arg.name;
    if (arg.kind === 'package') {
      return (
        <TWSelect
          key={arg.name}
          label={argLabel}
          placeholder="Choose an app"
          allowClear={false}
          showSearch={false}
          options={arg.options.map((value) => ({ value, label: PACKAGE_LABELS[value] || value }))}
          value={values[arg.name]}
          onChange={(value) => setValue(arg.name, value)}
          error={fieldError}
        />
      );
    }
    return (
      <TWInput
        key={arg.name}
        type="number"
        label={argLabel}
        min={arg.min}
        max={arg.max}
        step={1}
        value={values[arg.name] ?? ''}
        onChange={(e) => setValue(arg.name, e.target.value)}
        hint={`Between ${arg.min} and ${arg.max}`}
        error={fieldError}
      />
    );
  };

  return (
    <>
      <TWModal
        open={open}
        onClose={onClose}
        dismissible={!confirmOpen && !sending}
        size="xl"
        title="Send command"
        description={`To ${label}`}
        footer={
          <div className="flex justify-end gap-2">
            <TWButton variant="default" onClick={onClose} disabled={sending}>
              Cancel
            </TWButton>
            <TWButton variant="primary" onClick={onSend} disabled={!canSend} loading={sending}>
              Send
            </TWButton>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <TWSelect
              label="Command"
              allowClear={false}
              virtual={false}
              options={typeOptions(endMaintenanceSupported)}
              value={command.type}
              onChange={changeType}
            />
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              {command.description}
            </p>
          </div>

          {command.args.map(renderArg)}

          {command.risky ? (
            <div className="flex items-center gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-300">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
              <span>Interrupts the device</span>
            </div>
          ) : null}

          {needsTypedConfirm(count) ? (
            <TWInput
              label={`Type ${count} to confirm`}
              hint={`This affects ${count} devices.`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              inputMode="numeric"
            />
          ) : null}
        </div>
      </TWModal>

      <TWConfirmModal
        open={open && confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirm}
        tone="danger"
        title={`Send ${command.label} to ${devicesText(count)}?`}
        description={RISKY_DESCRIPTION}
        confirmLabel="Send"
        loading={sending}
      />
    </>
  );
};

export default CommandSheet;
